import { MaintenanceCustomFilter } from "@/api/resources/maintenances/types";
import { MaintenanceItemStatus, MaintenanceStatus, type Maintenance, type MaintenanceItem } from "@/supabase.types";
import { getDateDaysInThePast, getDateDaysInTheFuture, getDateEndOfDay, getDateStartOfDay } from "@/utils/dates";

export const isMaintenanceOfStrategy = (maintenance?: Maintenance | null) =>
  Boolean(maintenance && Boolean(maintenance.maintenance_strategy_id));

export const isMaintenanceCompleted = (maintenance?: Maintenance | null) =>
  Boolean(
    maintenance &&
    [MaintenanceStatus.finished, MaintenanceStatus.skipped].includes(maintenance.status as MaintenanceStatus),
  );
export const isMaintenanceScheduled = (maintenance?: Maintenance | null) =>
  maintenance?.status === MaintenanceStatus.scheduled;
export const isMaintenanceStarted = (maintenance?: Maintenance | null) =>
  maintenance?.status === MaintenanceStatus.started;
export const isMaintenanceFinished = (maintenance?: Maintenance | null) =>
  maintenance?.status === MaintenanceStatus.finished;
export const isMaintenanceSkipped = (maintenance?: Maintenance | null) =>
  maintenance?.status === MaintenanceStatus.skipped;

export const isMaintenanceItemCompleted = (maintenanceItem?: MaintenanceItem | null) =>
  Boolean(
    maintenanceItem &&
    [MaintenanceItemStatus.completed, MaintenanceItemStatus.failed, MaintenanceItemStatus.skipped].includes(
      maintenanceItem.status as MaintenanceItemStatus,
    ),
  );
export const isMaintenanceItemScheduled = (maintenanceItem?: MaintenanceItem | null) =>
  maintenanceItem?.status === MaintenanceItemStatus.pending;
export const isMaintenanceItemStarted = (maintenanceItem?: MaintenanceItem | null) =>
  maintenanceItem?.status === MaintenanceItemStatus.started;
export const isMaintenanceItemFinished = (maintenanceItem?: MaintenanceItem | null) =>
  maintenanceItem?.status === MaintenanceItemStatus.completed;
export const isMaintenanceItemFailed = (maintenanceItem?: MaintenanceItem | null) =>
  maintenanceItem?.status === MaintenanceItemStatus.failed;
export const isMaintenanceItemSkipped = (maintenanceItem?: MaintenanceItem | null) =>
  maintenanceItem?.status === MaintenanceItemStatus.skipped;

export const hasMaintenanceScheduled = (maintenances: Maintenance[]) => {
  return maintenances.some((maintenance) => isMaintenanceScheduled(maintenance));
};

export const hasMaintenanceStarted = (maintenances: Maintenance[]) => {
  return maintenances.some((maintenance) => isMaintenanceStarted(maintenance));
};

export const hasMaintenanceSkipped = (maintenances: Maintenance[]) => {
  return maintenances.some((maintenance) => isMaintenanceSkipped(maintenance));
};

export const hasMaintenanceCompleted = (maintenances: Maintenance[]) => {
  return maintenances.some((maintenance) => isMaintenanceCompleted(maintenance));
};

// Un-scoped views (History/Open) default to only non-deleted deployments, matching the
// pre-backend-migration app's always-on `filteredDeployments` filter — a manual selection always
// wins (and can't include a deleted deployment, since the picker excludes them); scoped views
// (HistoryStrategy/DeviceDataTrigger) get no deployment filter at all, matching the old app too.
export const resolveDeploymentIds = (
  selectedDeploymentIds: string[],
  isScoped: boolean,
  activeDeploymentIds: string[] | undefined,
): string[] | undefined => {
  if (isScoped) return undefined;
  return selectedDeploymentIds.length > 0 ? selectedDeploymentIds : activeDeploymentIds;
};

// Due/Today/Unassigned only make sense for view="open" (see FilterBarFormMaintenanceCustomFilter) —
// Notes/PartsUsed apply regardless of status, so they're also allowed for History/Upcoming views
export const isStatusAgnosticMaintenanceCustomFilter = (customFilter?: MaintenanceCustomFilter) =>
  customFilter === MaintenanceCustomFilter.Notes || customFilter === MaintenanceCustomFilter.PartsUsed;

// single source of truth for the Open/Upcoming/History pages' default mDateFrom/mDateUntil range —
// used both when the user clears the date picker (MaintenanceDataPage's setSafeDateHistory/
// setSafeDateUpcoming) and when global filters get reset to defaults (GlobalFiltersProvider's
// resetOptions), so the two can't drift out of sync with each other
export const getDefaultMaintenanceUpcomingDateRange = (): [string, string] => [
  getDateStartOfDay(),
  getDateEndOfDay(getDateDaysInTheFuture(30)),
];

export const getDefaultMaintenanceHistoryDateRange = (): [string, string] => [
  getDateEndOfDay(getDateDaysInThePast(90)),
  getDateEndOfDay(),
];
